import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CmsContentPage } from '@coin/shared/cms/util';
import { environment } from '@coin/shared/core/util-environments';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Fn } from '@coin/shared/core/util-models';

@Injectable({
  providedIn: 'root'
})
export class CmsContentPagesService {
  private readonly toast = inject(ToastrService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  highlightInEditMode: { scrollAndExpandItem: Fn } = null;

  /** Get Link to latest content page item (avoid Cloudfront to be sure that it is the latest ) */
  public getContentPageItem(): Observable<Record<string, CmsContentPage>> {
    return this.http
      .get(`${environment.api.cmsService}content-pages/download-url/Draft`)
      .pipe(switchMap(linkData => this.getContentPageItemFile(linkData['link'])));
  }

  /** Use the link to get the respective file */
  public getContentPageItemFile(link: string): Observable<Record<string, CmsContentPage>> {
    return this.http.get<Record<string, CmsContentPage>>(link);
  }

  public updateDraft(content: Record<string, CmsContentPage>): void {
    this.http
      .get(`${environment.api.cmsService}content-pages/upload-url/Draft`)
      .pipe(switchMap(cpUploadLink => this.updateDraftFile(cpUploadLink['link'], content)))
      .pipe(switchMap(() => this.finalizeRequests('Updated News Item')))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private updateDraftFile(cpUploadLink: string, content: unknown): Observable<unknown> {
    return this.http.put(cpUploadLink, JSON.stringify(content));
  }

  private finalizeRequests(text: string): string {
    this.toast.success(text);
    this.invalidation();
    return '';
  }

  public publishObject(content: Record<string, CmsContentPage>): void {
    this.http
      .get(`${environment.api.cmsService}content-pages/upload-url/Published`)
      .pipe(switchMap(cpUploadLink => this.publishObjectFile(cpUploadLink['link'], content)))
      .pipe(switchMap(() => this.finalizeRequests('Published Content page update.')))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public publishObjectFile(cpUploadLink: string, content: Record<string, CmsContentPage>): Observable<unknown> {
    return this.http.put(cpUploadLink, JSON.stringify(content));
  }

  private invalidation(): void {
    this.http
      .post(`${environment.api.cmsService}invalidation`, null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toast.success('Invalidation started. Your changes should soon be visible.');
      });
  }
}
